// **The committed ambient loop, bundled** (`client-home`; plan **D9** and **D20**).
//
// Home holds no credential when it first paints, so the authenticated trace routes are unreachable
// to it — and an unauthenticated demo-trace route would be a new public read surface for
// decoration. The loop is therefore a static asset that ships *inside the bundle*, so the
// background is present on first paint with **no request of any kind**, and nothing about it
// depends on `server-static-client`'s asset-delivery reshaping.
//
// **`?inline`, not `?url`.** A URL import would still cost a same-origin `fetch`, which
// `client-home` forbids ("it SHALL NOT require any server request"). Vite's explicit `?inline`
// query forces the bytes into the bundle as a `data:` URI regardless of the inline size limit, and
// this module decodes it once.
//
// ## What is pinned, and where the pins are checked
//
// The asset is a golden with teeth (review **A5**): a **pinned generator command and seed**, a
// **committed checksum**, a **regeneration procedure**, and a **CI byte budget** — all four
// recorded in `docs/dev/ambient-background.md` and asserted by `ambient-asset.test.ts`. The
// runtime degrades silently when the asset is missing or malformed (**D10**), so the tests are the
// only place a break can surface. That asymmetry is the design: the fallback protects the
// *player*, the checks protect the *developer*.

import ambientLoopUri from './ambient-loop.bin?inline';

/** The committed asset's bytes, or `null` when no asset is bundled.
 *
 * Returning `null` rather than throwing is the whole degradation contract: `AmbientBackground`
 * reads it as "draw the static season-ground fill", with no spinner, no error and no retry. A
 * build that has not generated the asset therefore still runs, and still shows a deliberate-looking
 * screen — while `ambient-asset.test.ts` fails, which is where the absence is meant to be loud. */
export function ambientLoopBytes(): Uint8Array | null {
  return decodeInlined(ambientLoopUri);
}

/** Decodes a `data:...;base64,...` URI to bytes, or `null` for anything that is not one. */
function decodeInlined(uri: string): Uint8Array | null {
  const marker = ';base64,';
  const at = uri.indexOf(marker);
  if (at === -1) {
    return null;
  }
  try {
    const binary = atob(uri.slice(at + marker.length));
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  } catch {
    // Not decodable. Same outcome as absent: the background degrades and CI complains.
    return null;
  }
}
